'use strict'
import { useCallback, useEffect, useMemo, useState } from 'react'
import axios from 'axios'
import { CircularProgress, Tooltip } from '@material-ui/core'
import { DFSearchInput, DFToggle, DFFilterChip } from '../../df'
import { getRouteColor } from './crewColors'
import { useTerritory } from './TerritoryContext'

/* ── Inline SVG icons ───────────────────────────────────────────── */
const RefreshSvg = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="23 4 23 10 17 10" /><polyline points="1 20 1 14 7 14" />
    <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
  </svg>
)

const LinkSvg = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71" />
    <path d="M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71" />
  </svg>
)

const OpenSvg = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6" />
    <path d="M15 3h6v6" />
    <path d="M10 14L21 3" />
  </svg>
)

/* ── Sort helpers ────────────────────────────────────────────────── */
function descendingComparator (a, b, orderBy) {
  const av = a[orderBy]
  const bv = b[orderBy]
  if (av == null && bv == null) return 0
  if (av == null) return 1
  if (bv == null) return -1
  if (typeof av === 'string') return bv.localeCompare(av)
  return bv < av ? -1 : bv > av ? 1 : 0
}

function getComparator (order, orderBy) {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy)
}

/* ── Sort arrow SVG ──────────────────────────────────────────────── */
const SortArrow = ({ active, direction }) => (
  <svg width="10" height="10" viewBox="0 0 24 24" fill="none"
    stroke={active ? 'var(--df-primary)' : 'var(--df-ink-4)'}
    strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
    style={{ marginLeft: 4, opacity: active ? 1 : 0.4, transition: 'transform .12s', transform: direction === 'desc' ? 'rotate(180deg)' : 'none' }}
  >
    <polyline points="18 15 12 9 6 15" />
  </svg>
)

export default function RoutesTab ({ url, onSnackbar, onRoutesChanged }) {
  const { routeIds } = useTerritory()
  const [routesList, setRoutesList] = useState([])
  const [meta, setMeta] = useState(null)
  const [loading, setLoading] = useState(false)
  const [search, setSearch] = useState('')
  const [showExcluded, setShowExcluded] = useState(true)
  const [showOnlyWithoutCrew, setShowOnlyWithoutCrew] = useState(false)
  const [order, setOrder] = useState('asc')
  const [orderBy, setOrderBy] = useState('routeName')
  const [pendingToggle, setPendingToggle] = useState(null)

  const openAspireSync = (route = null) => {
    const params = new URLSearchParams({ tab: 'routes' })
    if (route?.routeId) params.set('routeId', route.routeId)
    if (route?.routeName) params.set('route', route.routeName)
    window.open(`/aspire-sync?${params.toString()}`, '_blank', 'noopener,noreferrer')
  }

  const fetchRoutes = useCallback(async () => {
    setLoading(true)
    try {
      const resp = await axios.get(`${url}/api/hmi/territory/routes`)
      setRoutesList(resp.data?.routes || [])
      setMeta(resp.data?.meta || null)
    } catch (err) {
      console.error('Error loading routes:', err)
      if (onSnackbar) onSnackbar(err.response?.data?.message || err.message || 'Failed to load routes', 'error')
    } finally {
      setLoading(false)
    }
  }, [url, onSnackbar])

  useEffect(() => {
    fetchRoutes()
  }, [fetchRoutes])

  const handleToggleExclude = async (route) => {
    const newExclude = !route.excludeFromTerritory
    setPendingToggle(route._id)
    try {
      await axios.patch(`${url}/api/hmi/territory/routes/${route._id}/exclude`, {
        exclude: newExclude
      })
      // Update local state
      setRoutesList(prev => prev.map(r =>
        r._id === route._id ? { ...r, excludeFromTerritory: newExclude } : r
      ))
      setMeta(prev => {
        if (!prev) return prev
        return {
          ...prev,
          excluded: prev.excluded + (newExclude ? 1 : -1),
          included: prev.included + (newExclude ? -1 : 1)
        }
      })
      if (onSnackbar) {
        onSnackbar(
          newExclude
            ? `${route.routeName || 'Route'} excluded from territory`
            : `${route.routeName || 'Route'} included in territory`,
          'success'
        )
      }
      if (onRoutesChanged) onRoutesChanged()
    } catch (err) {
      console.error('Error toggling exclude:', err)
      if (onSnackbar) onSnackbar(err.response?.data?.message || err.message || 'Failed to update route', 'error')
    } finally {
      setPendingToggle(null)
    }
  }

  const handleSort = (column) => {
    const isAsc = orderBy === column && order === 'asc'
    setOrder(isAsc ? 'desc' : 'asc')
    setOrderBy(column)
  }

  const filteredRoutes = useMemo(() => {
    let filtered = routesList
    if (!showExcluded) {
      filtered = filtered.filter(r => !r.excludeFromTerritory)
    }
    if (showOnlyWithoutCrew) {
      filtered = filtered.filter(r => !r.crewId)
    }
    if (search.trim()) {
      const q = search.trim().toLowerCase()
      filtered = filtered.filter(r =>
        (r.routeName || '').toLowerCase().includes(q) ||
        (r.crewLeaderName || '').toLowerCase().includes(q) ||
        (r.crewName || '').toLowerCase().includes(q) ||
        (r.divisionName || '').toLowerCase().includes(q) ||
        String(r.routeId || '').includes(q)
      )
    }
    return filtered.slice().sort(getComparator(order, orderBy))
  }, [routesList, showExcluded, showOnlyWithoutCrew, search, order, orderBy])

  const columns = [
    { id: 'routeName', label: 'Route Name', sortable: true },
    { id: 'crewLeaderName', label: 'Crew Lead', sortable: true },
    { id: 'crewName', label: 'Linked Crew', sortable: true },
    { id: 'routeSize', label: 'Size', sortable: true },
    { id: 'divisionName', label: 'Division', sortable: true },
    { id: 'routeId', label: 'Aspire ID', sortable: true },
    { id: 'excludeFromTerritory', label: 'Include', sortable: false }
  ]

  const includedCount = meta?.included ?? routesList.filter(r => !r.excludeFromTerritory).length
  const excludedCount = meta?.excluded ?? routesList.filter(r => r.excludeFromTerritory).length

  return (
    <div className="content-pad" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {/* ── Toolbar ────────────────────────────────────────────── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <DFSearchInput
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search routes…"
        />
        <div style={{ display: 'flex', gap: 6 }}>
          <DFFilterChip
            label={`${includedCount} included`}
            active={!showExcluded}
            onClick={() => setShowExcluded(false)}
          />
          <DFFilterChip
            label={`${excludedCount} excluded`}
            active={showExcluded && excludedCount > 0}
            warn={excludedCount > 0 && showExcluded}
            onClick={() => setShowExcluded(true)}
          />
          <DFFilterChip
            label={`${meta?.withoutCrew ?? routesList.filter(r => !r.crewId).length} no crew`}
            active={showOnlyWithoutCrew}
            warn={(meta?.withoutCrew ?? routesList.filter(r => !r.crewId).length) > 0}
            onClick={() => setShowOnlyWithoutCrew(prev => !prev)}
          />
        </div>
        {meta && meta.withoutCrew > 0 && (
          <span style={{
            display: 'inline-flex', alignItems: 'center', padding: '3px 10px', borderRadius: 99,
            fontSize: 11, fontWeight: 600, background: 'var(--df-warn-bg, #fff3d9)', color: 'var(--df-warn-text, #8c5b00)'
          }}>
            {meta.withoutCrew} no crew
          </span>
        )}
        {meta && meta.withoutCrew > 0 && (
          <button
            type="button"
            onClick={() => openAspireSync()}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 10px',
              borderRadius: 6, border: '1px solid var(--df-line-2)', background: 'var(--df-surface)',
              color: 'var(--df-primary)', cursor: 'pointer', fontSize: 12, fontWeight: 600,
              fontFamily: 'inherit', flexShrink: 0
            }}
          >
            <OpenSvg />
            Open Aspire Sync
          </button>
        )}
        <button
          onClick={fetchRoutes}
          disabled={loading}
          title="Refresh"
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center', width: 32, height: 32,
            borderRadius: 6, border: '1px solid var(--df-line-2)', background: 'var(--df-surface)',
            color: 'var(--df-ink-3)', cursor: loading ? 'not-allowed' : 'pointer', flexShrink: 0,
            opacity: loading ? 0.5 : 1
          }}
        >
          {loading ? <CircularProgress size={14} style={{ color: 'var(--df-primary)' }} /> : <RefreshSvg />}
        </button>
      </div>

      {/* ── Table ──────────────────────────────────────────────── */}
      {loading && !routesList.length ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
          <CircularProgress size={28} style={{ color: 'var(--df-primary)' }} />
        </div>
      ) : (
        <div className="rw-card" style={{ overflow: 'hidden' }}>
          <div className="tbl-wrap" style={{ maxHeight: 'calc(100vh - 300px)' }}>
            <table className="tbl">
              <thead>
                <tr>
                  {columns.map(col => (
                    <th
                      key={col.id}
                      onClick={col.sortable ? () => handleSort(col.id) : undefined}
                      style={{ cursor: col.sortable ? 'pointer' : 'default', userSelect: 'none' }}
                    >
                      <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                        {col.label}
                        {col.sortable && <SortArrow active={orderBy === col.id} direction={orderBy === col.id ? order : 'asc'} />}
                      </span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredRoutes.map(route => {
                  const isExcluded = route.excludeFromTerritory
                  const dotColor = getRouteColor(route._id, routeIds || [])

                  return (
                    <tr
                      key={route._id}
                      style={{ opacity: isExcluded ? 0.45 : 1, transition: 'opacity .15s' }}
                    >
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span style={{
                            display: 'inline-block', width: 9, height: 9, borderRadius: '50%', flexShrink: 0,
                            backgroundColor: dotColor
                          }} />
                          <span style={{ fontWeight: 600, fontSize: 13, color: 'var(--df-ink)' }}>
                            {route.routeName || '-'}
                          </span>
                        </div>
                      </td>
                      <td style={{ color: 'var(--df-ink-3)', fontSize: 12 }}>
                        {route.crewLeaderName || '-'}
                      </td>
                      <td style={{ fontSize: 12 }}>
                        {route.crewName ? (
                          <Tooltip title={`Crew ID: ${route.crewId || '?'}`} arrow>
                            <span style={{ color: 'var(--df-ink-2)' }}>{route.crewName}</span>
                          </Tooltip>
                        ) : (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                            <span style={{ color: 'var(--df-warn-text, #8c5b00)', fontSize: 12 }}>No crew linked</span>
                            <button
                              type="button"
                              onClick={() => openAspireSync(route)}
                              title="Open route matching in Aspire Sync"
                              style={{
                                display: 'inline-flex', alignItems: 'center', gap: 4,
                                padding: '3px 7px', borderRadius: 5,
                                border: '1px solid var(--df-warn-border, #e3bf73)',
                                background: 'var(--df-warn-bg, #fff3d9)',
                                color: 'var(--df-warn-text, #8c5b00)',
                                cursor: 'pointer', fontSize: 11, fontWeight: 700,
                                fontFamily: 'inherit'
                              }}
                            >
                              <LinkSvg />
                              Link crew
                            </button>
                          </div>
                        )}
                      </td>
                      <td style={{ fontSize: 12, color: 'var(--df-ink-2)' }}>
                        {route.routeSize || '-'}
                      </td>
                      <td style={{ fontSize: 12, color: 'var(--df-ink-3)' }}>
                        {route.divisionName || '-'}
                      </td>
                      <td style={{ fontSize: 12, color: 'var(--df-ink-3)' }}>
                        {route.routeId || '-'}
                      </td>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <DFToggle
                            checked={!isExcluded}
                            onChange={() => handleToggleExclude(route)}
                            disabled={pendingToggle === route._id}
                          />
                          {pendingToggle === route._id && (
                            <CircularProgress size={14} style={{ color: 'var(--df-primary)' }} />
                          )}
                        </div>
                      </td>
                    </tr>
                  )
                })}
                {filteredRoutes.length === 0 && !loading && (
                  <tr>
                    <td colSpan={columns.length} style={{ textAlign: 'center' }}>
                      <div style={{ padding: 24, color: 'var(--df-ink-4)', fontSize: 13 }}>
                        {search ? 'No routes match your search.' : 'No routes found.'}
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  )
}
